import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../shared/types';
import { getAuthHeadersFromUserData } from '../shared/utils/auth-headers.util';

export interface DashboardTransactionItem {
  id: string;
  paymentDate: string;
  orderId: string;
  publicOrderId: string;
  customerName?: string;
  customerIdentifier: string;
  washType?: string;
  locationName?: string;
  cardLast4: string;
  cardType: string;
  paymentMethodType?: string;
  /** Null when the payment has no Stripe reference — a fully discounted $0 invoice. */
  transactionId: string | null;
  /** Dollars discounted on THIS payment; null when nothing was discounted. */
  discountAmount?: number | null;
  /** 'percentage' | 'flat'. */
  discountType?: string;
  /** Percent off, or dollars off for a flat discount, as THIS invoice applied it. */
  discountValue?: number | null;
  status: string;
  baseAmount: number;
  convenienceFee: number;
  platformFee?: number | null;
  refunds?: { id: string; amount: number; paymentDate: string | null }[];
}

export interface DashboardTransactionsResponse {
  items: DashboardTransactionItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface DashboardMetrics {
  totalRevenue: number;
  netEarnings: number;
  pendingPayouts: number;
  completedPayouts: number;
  refunds: { total: number; count: number };
}

/** CWS-2012: the Pending Payouts card, read on its own so the other cards never wait on Stripe. */
export interface DashboardPendingPayouts {
  pendingPayouts: number;
  /** 'stripe' is the live balance; 'db' is the mirror fallback. */
  source: 'stripe' | 'db';
}

export interface PayoutListItem {
  /** CWS-1684: 'balance_available' — pre-switch money available in Stripe, waiting for our payouts. */
  resourceType: 'payout' | 'balance_transaction' | 'balance_available';
  id: string;
  amount: number;
  currency: string;
  status: string;
  stripeStatus: string;
  created: string;
  arrivalDate: string | null;
  displayDate: string;
  locationName?: string;
  bankLast4: string | null;
  bankName: string | null;
  bankDisplay: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  /** Stripe's own scheduled payout (not one our payout cron or someone made by hand). */
  automatic?: boolean;
}

export interface PayoutListResponse {
  items: PayoutListItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface DailySettlementItem {
  id: string;
  businessDate: string;
  locationName: string;
  grossAmount: number;
  feeAmount: number;
  refundAmount: number;
  netAmount: number;
  status: 'pending' | 'processing' | 'paid';
  /** CWS-729: 'stripe_auto' = paid by Stripe's automatic payouts (never our cron); 'stripe_manual' = our payout cron. */
  payoutMode: 'stripe_auto' | 'stripe_manual';
  /** CWS-729: a pre-switch day no Stripe payout covered, taken over and paid by our payout cron. */
  preSwitch?: boolean;
  /** CWS-729: the location's most recent before-switch day in this listing — the switch divider sits above it. */
  isSwitchBoundary?: boolean;
  /** CWS-729: the location's first daily-payout business day (YYYY-MM-DD); null when never switched. */
  switchDate?: string | null;
  /** IANA timezone of the location — the switch happens at 12:00 AM local. */
  locationTimezone?: string | null;
  stripePayoutId: string | null;
  /** Live Stripe status of the linked payout ('pending' | 'in_transit' | 'paid' | ...); null until linked/mirrored. */
  payoutStatus: string | null;
  bankName: string | null;
  bankLast4: string | null;
  arrivalDate: string | null;
  /** CWS-1615: when Stripe reported the covering payout paid — the day shown as Completed. */
  paidAt?: string | null;
  /** CWS-1615: every payout that holds some of the day's money; more than one means a split or pre-switch day. */
  payoutParts?: PayoutPart[];
  /** CWS-1615: what the whole day pays out — the row's net plus, on a pre-switch day, what Stripe paid before the takeover. */
  totalPayoutAmount?: number;
  /** Refunds beyond the day's money, taken from the next day's payout (dollars). */
  refundCarryoverOut?: number;
  /** The day that takes them (YYYY-MM-DD); null while it has no settlement yet. */
  refundCarryoverTo?: string | null;
  /** Refunds left over from the day before, taken from this day's payout (dollars). */
  refundCarryoverIn?: number;
  /** The day they came from (YYYY-MM-DD). */
  refundCarryoverFrom?: string | null;
  /** Late payments of earlier paid days added to this day's payout (dollars). */
  latePaymentCarryIn?: number;
  /** Payments recorded after this day was paid, moved on to a later payout (dollars). */
  latePaymentCarryOut?: number;
}

/** CWS-1615: one payout's share of a business day's money. */
export interface PayoutPart {
  /** 'stripe' = a Stripe automatic payout; 'ours' = our payout cron's. */
  source: 'stripe' | 'ours';
  amount: number;
  /** True once that payout reached the bank. */
  paid: boolean;
  stripePayoutId: string | null;
  /** The payout's expected (or actual) arrival, when one exists and is alive. */
  arrivalDate: string | null;
  /** The payout failed or was canceled; Stripe's next automatic payout retries the money. */
  failed?: boolean;
  /** When Stripe reported this payout paid, when known; the line shows this day over the arrival date. */
  paidAt?: string | null;
}

/** Payout Tracker: a payout no day lists (or Stripe's pending balance) — it covers several days, so no business day. */
export interface TrackerPayoutItem extends PayoutListItem {
  kind: 'payout';
  /** The first payout from before a location's switch to manual payouts: the switch divider sits above it. */
  isSwitchBoundary?: boolean;
  switchDate?: string | null;
  /** The switch moment (ISO): the divider's label shows its time. */
  switchedAt?: string | null;
  locationTimezone?: string | null;
}

/** Payout Tracker: the days our payout cron pays, then Stripe's own payouts. */
export type CombinedPayoutItem = (DailySettlementItem & { kind: 'day' }) | TrackerPayoutItem;

export interface CombinedPayoutsResponse {
  items: CombinedPayoutItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface DashboardFilters {
  filterBy?: string;
  startDate?: string;
  endDate?: string;
  status?: string[];
  stripeAccountId?: string;
  locationId?: string;
  timezone?: string;
}

@Injectable({ providedIn: 'root' })
export class PaymentsDashboardService {
  private readonly baseUrl = `${environment.baseUrl}/${environment.apiVersion}`;

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return getAuthHeadersFromUserData();
  }

  private postDashboard<T>(path: string, body: any): Observable<T | null> {
    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}${path}`, body, { headers: this.getHeaders() })
      .pipe(map(res => (res?.status && res.data ? res.data : null)));
  }

  private postDashboardBlob(path: string, body: any): Observable<Blob> {
    return this.http.post(`${this.baseUrl}${path}`, body, {
      headers: this.getHeaders(),
      responseType: 'blob',
    });
  }

  getDashboardTransactions(body: {
    paging?: { page?: number; limit?: number };
    filters?: DashboardFilters;
  }): Observable<DashboardTransactionsResponse | null> {
    return this.postDashboard<DashboardTransactionsResponse>(
      '/payment/dashboard/transactions',
      body
    );
  }

  getDashboardMetrics(body?: { filters?: DashboardFilters }): Observable<DashboardMetrics | null> {
    return this.postDashboard<DashboardMetrics>('/payment/dashboard/metrics', body ?? {});
  }

  getPendingPayouts(body?: {
    filters?: DashboardFilters;
  }): Observable<DashboardPendingPayouts | null> {
    return this.postDashboard<DashboardPendingPayouts>(
      '/payment/dashboard/pending-payouts',
      body ?? {}
    );
  }

  listPayouts(body?: {
    paging?: { page?: number; limit?: number };
    filters?: DashboardFilters;
  }): Observable<PayoutListResponse | null> {
    return this.postDashboard<PayoutListResponse>('/payment/payouts', body ?? {});
  }

  listCombinedPayouts(body?: {
    paging?: { page?: number; limit?: number };
    filters?: { locationId?: string; status?: string[] };
  }): Observable<CombinedPayoutsResponse | null> {
    return this.postDashboard<CombinedPayoutsResponse>('/payment/payouts/combined', body ?? {});
  }

  exportDashboardTransactions(body: { filters?: DashboardFilters }): Observable<Blob> {
    return this.postDashboardBlob('/payment/dashboard/transactions/export', body);
  }

  exportPayouts(body: { filters?: DashboardFilters }): Observable<Blob> {
    return this.postDashboardBlob('/payment/payouts/export', body);
  }

  /** Payout Tracker CSV: every row listCombinedPayouts pages through. */
  exportDailySettlements(body: {
    filters?: { locationId?: string; status?: string[] };
  }): Observable<Blob> {
    return this.postDashboardBlob('/payment/payouts/daily-settlements/export', body);
  }
}
